import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { PARTY_REVISION_CONFLICT_MESSAGE, revisionConflictFromHttp } from '../../core/http/revision-conflict';
import { paymentNotReadyFromHttp, paymentsUnavailableFromHttp } from '../../core/http/payment-errors';
import { PartyCaseDraft, PartyCaseFeeActivityHistory, PartyCaseListItem, PartyCaseOverview, PartyCheckoutSession, PartyDocument } from './party-case.models';
import { DocumentAssociation, PartyCaseService } from './party-case.service';

@Injectable({ providedIn: 'root' })
export class HttpPartyCaseService implements PartyCaseService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/party-cases`;

  listCases(): Observable<readonly PartyCaseListItem[]> {
    return this.http.get<readonly PartyCaseListItem[]>(this.baseUrl);
  }

  getCase(caseNumber: string): Observable<PartyCaseOverview | null> {
    return this.getCaseByUrl(this.caseUrl(caseNumber));
  }

  getCaseFeeActivity(caseNumber: string): Observable<PartyCaseFeeActivityHistory> {
    return this.http.get<PartyCaseFeeActivityHistory>(`${this.caseUrl(caseNumber)}/fee-activity`);
  }

  saveDraft(caseNumber: string, draft: PartyCaseDraft, expectedRevision?: number): Observable<PartyCaseOverview | null> {
    return this.http.put<PartyCaseOverview>(`${this.caseUrl(caseNumber)}/draft`, { expectedRevision, ...draft }).pipe(this.nullOnClientError());
  }

  /** Submit verifies an unlocked submission's fee with ForthrightPayments first, so it can answer 503. */
  submitCase(caseNumber: string, expectedRevision?: number): Observable<PartyCaseOverview | null> {
    return this.http.post<PartyCaseOverview>(`${this.caseUrl(caseNumber)}/submission`, { expectedRevision }).pipe(this.unavailableOnServiceError(), this.nullOnClientError());
  }

  startCheckout(caseNumber: string): Observable<PartyCheckoutSession> {
    return this.http.post<PartyCheckoutSession>(`${this.caseUrl(caseNumber)}/payments/checkout`, {}).pipe(
      catchError((error: unknown) => {
        const mapped = paymentNotReadyFromHttp(error) ?? paymentsUnavailableFromHttp(error);
        return throwError(() => mapped ?? error);
      }),
    );
  }

  refreshPayment(caseNumber: string, expectedRevision: number): Observable<PartyCaseOverview | null> {
    return this.http
      .post<PartyCaseOverview>(`${this.caseUrl(caseNumber)}/payments/refresh`, { expectedRevision })
      .pipe(this.unavailableOnServiceError(), this.nullOnClientError());
  }

  refreshCase(caseNumber: string): Observable<PartyCaseOverview | null> {
    return this.getCase(caseNumber);
  }

  confirmReviewPayment(caseNumber: string, expectedRevision: number, state: 'paid' | 'pending' | 'election-pending' | 'canceled' | 'failed'): Observable<PartyCaseOverview | null> {
    return this.http.post<PartyCaseOverview>(`${this.caseUrl(caseNumber)}/review-payment-confirmation`, { expectedRevision, state }).pipe(this.nullOnClientError());
  }

  saveInformationResponse(caseNumber: string, requestId: string, response: string, documents: readonly PartyDocument[], expectedRevision: number): Observable<PartyCaseOverview | null> {
    return this.http
      .put<PartyCaseOverview>(`${this.caseUrl(caseNumber)}/information-requests/${encodeURIComponent(requestId)}/response`, { response, documents, expectedRevision })
      .pipe(this.nullOnClientError());
  }

  sendContact(caseNumber: string, subject: string, message: string): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.caseUrl(caseNumber)}/contact`, { subject, message })
      .pipe(catchError((error: unknown) => (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 404) ? of(false) : throwError(() => error))));
  }

  submitCaseRequest(caseNumber: string, type: 'Settlement' | 'Withdrawal', note: string): Observable<PartyCaseOverview | null> {
    return this.http.post<PartyCaseOverview>(`${this.caseUrl(caseNumber)}/requests`, { type, note }).pipe(this.nullOnClientError());
  }

  /**
   * Asks for the bytes, not a JSON envelope. The API streams the file through itself rather than
   * handing out a storage URL, so this is an ordinary authenticated request and the bearer token the
   * interceptor attaches is what authorizes it.
   */
  openDocument(caseNumber: string, documentId: string): Observable<Blob | null> {
    return this.http.get(`${this.caseUrl(caseNumber)}/documents/${encodeURIComponent(documentId)}`, { responseType: 'blob' }).pipe(this.nullOnNotFound());
  }

  /**
   * Multipart, so the file streams rather than being base64'd into a JSON body — which at the
   * configured cap would be a third of a gigabyte of string.
   *
   * No Content-Type header is set on purpose: the browser has to add the multipart boundary itself,
   * and setting it by hand produces a request the server cannot parse.
   */
  uploadDocument(caseNumber: string, file: File, association?: DocumentAssociation): Observable<PartyCaseOverview | null> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (association?.lineItemId) form.append('lineItemId', association.lineItemId);
    if (association?.caseRequestId) form.append('caseRequestId', association.caseRequestId);

    return this.http.post<PartyCaseOverview>(`${this.caseUrl(caseNumber)}/documents`, form).pipe(this.nullOnClientError());
  }

  deleteDocument(caseNumber: string, documentId: string): Observable<PartyCaseOverview | null> {
    return this.http.delete<PartyCaseOverview>(`${this.caseUrl(caseNumber)}/documents/${encodeURIComponent(documentId)}`).pipe(this.nullOnClientError());
  }

  private getCaseByUrl(url: string): Observable<PartyCaseOverview | null> {
    return this.http.get<PartyCaseOverview>(url).pipe(this.nullOnNotFound());
  }

  private caseUrl(caseNumber: string): string {
    return `${this.baseUrl}/${encodeURIComponent(caseNumber)}`;
  }

  private nullOnNotFound<T>() {
    return (source: Observable<T>): Observable<T | null> =>
      source.pipe(catchError((error: unknown) => (error instanceof HttpErrorResponse && error.status === 404 ? of(null) : throwError(() => error))));
  }

  private unavailableOnServiceError<T>() {
    return (source: Observable<T>): Observable<T> =>
      source.pipe(
        catchError((error: unknown) => {
          const unavailable = paymentsUnavailableFromHttp(error);
          return throwError(() => unavailable ?? error);
        }),
      );
  }

  private nullOnClientError<T>() {
    return (source: Observable<T>): Observable<T | null> =>
      source.pipe(
        catchError((error: unknown) => {
          const conflict = revisionConflictFromHttp(error, PARTY_REVISION_CONFLICT_MESSAGE);
          if (conflict) return throwError(() => conflict);
          return error instanceof HttpErrorResponse && (error.status === 400 || error.status === 404) ? of(null) : throwError(() => error);
        }),
      );
  }
}
